import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import { IDynamicTableState } from '../../../interfaces/tables';
import { changeDynamicTablePage, tablesAtom } from '../../../store/recoil/TableState';

function generatorPagination(currentPage: number, pagesTotal: number) {
  const listPaginator: any = [1];

  if (pagesTotal <= 5) {
    let num = 1;
    while (num < pagesTotal) {
      num += 1;
      listPaginator.push(num);
    }
  } else if (pagesTotal > 5 && currentPage === 1) {
    listPaginator.push(currentPage + 1);
    listPaginator.push(currentPage + 2);
    listPaginator.push('...');
    listPaginator.push(pagesTotal);
  } else if (pagesTotal > 5 && currentPage === pagesTotal) {
    listPaginator.push('...');
    listPaginator.push(pagesTotal - 2);
    listPaginator.push(pagesTotal - 1);
    listPaginator.push(pagesTotal);
  } else if (pagesTotal > 5 && currentPage <= 4 && currentPage >= pagesTotal - 3) {
    let num = 1;
    while (num < pagesTotal) {
      num += 1;
      listPaginator.push(num);
    }
  } else if (pagesTotal > 5 && currentPage <= 4 && currentPage < pagesTotal - 3) {
    let num = 1;
    while (num < currentPage + 1) {
      num += 1;
      listPaginator.push(num);
    }
    listPaginator.push('...');
    listPaginator.push(pagesTotal);
  } else if (pagesTotal > 5 && currentPage > 4 && currentPage >= pagesTotal - 3) {
    listPaginator.push('...');
    let num = currentPage - 2;
    while (num < pagesTotal) {
      num += 1;
      listPaginator.push(num);
    }
  } else if (pagesTotal > 5 && currentPage > 4 && currentPage < pagesTotal - 3) {
    listPaginator.push('...');
    let num = currentPage - 2;
    while (num < currentPage + 1) {
      num += 1;
      listPaginator.push(num);
    }
    listPaginator.push('..');
    listPaginator.push(pagesTotal);
  }

  return listPaginator;
}

interface ITablePaginationProps {
  tableName: string;
}

export default function TablePagination(props: ITablePaginationProps) {
  const { formatMessage: f } = useIntl();
  const [tables, setTables] = useRecoilState<IDynamicTableState>(tablesAtom);

  // const [ currentPage, setCurrentPage ] = React.useState(tables[props.tableName].paging.currentPage);
  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false);
  const [list, setList] = React.useState<number[]>([10, 20, 30, 40, 50, 60, 70]);

  const { paging, records } = tables[props.tableName];

  const handleKeyboard = (event: any) => {
    if (event.keyCode === 27) {
      setIsMenuOpen(false);
    }
  };

  const handleClickOutside = (e: any) => {
    if (
      e.target.id !== 'dropdownMenu19' &&
      e.target.className !== 'pagingList' &&
      e.target.tagName !== 'I'
    ) {
      setIsMenuOpen(false);
    }
  };

  const handleNextPrevPage = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    event.preventDefault();
    const type = event.currentTarget.getAttribute('data-type');

    const next = type === 'prev' ? paging.currentPage - 1 : paging.currentPage + 1;

    if (next > 0 && next <= paging.pagesTotal) {
      const number = type === 'prev' ? paging.currentPage - 1 : paging.currentPage + 1;
      setTables((oldState: IDynamicTableState) => {
        return changeDynamicTablePage(oldState, {
          number,
          pagingType: 'currentPage',
          name: props.tableName,
        });
      });
    }
  };

  const handleOpenList = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    event.preventDefault();
    const number = event.currentTarget.getAttribute('data-number');
    if (number) {
      if (number !== '...' && number !== '..' && paging.currentPage !== number) {
        setTables((oldState: IDynamicTableState) => {
          return changeDynamicTablePage(oldState, {
            number: Number(number),
            pagingType: 'currentPage',
            name: props.tableName,
          });
        });
      }
    }
  };

  const handleSetPage = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    const number = event.currentTarget.getAttribute('data-number');
    if (number) {
      setIsMenuOpen(false);
      setTables((oldState: IDynamicTableState) => {
        return changeDynamicTablePage(oldState, {
          number: Number(number),
          pagingType: 'rowsPerPage',
          name: props.tableName,
        });
      });
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);
    document.addEventListener('keydown', handleKeyboard, false);

    return function cleanup() {
      document.removeEventListener('click', handleClickOutside, false);
      document.addEventListener('keydown', handleKeyboard, false);
    };
  }, []);

  const handleDataCy = (name: string) => {
    if (name.includes('ride_positions')) {
      return props.tableName.replace('-', '_count-');
    }
    return `${name}_count`;
  };

  // Reset paging if no records
  React.useEffect(() => {
    if (records.length === 0) {
      setTables((oldState) => ({
        ...oldState,
        [props.tableName]: {
          ...oldState[props.tableName],
          paging: {
            currentPage: 1,
            rowsPerPage: 10,
            rowsTotal: 0,
            pagesTotal: 0,
          },
        },
      }));
    }
  }, [paging.rowsTotal, setTables, props.tableName, records.length]);

  return (
    <nav className="text-center clearfix clearfix" style={{ paddingTop: 10, position: 'relative' }}>
      <ul className="pagination pagination-custom">
        {paging.currentPage > 1 && paging.pagesTotal > 1 && (
          <li
            className="prev-next prev"
            data-type="prev"
            onClick={handleNextPrevPage}
            data-cy="table-pagination-prev"
          >
            <a href="#" title={f({ id: 'table.pagination.prev' })}>
              <i className="cmd-icon cmd-icon-expander-arrow-left" />
            </a>
          </li>
        )}
        {paging.pagesTotal > 1 &&
          Object.values(
            generatorPagination(Number(paging.currentPage), Number(paging.pagesTotal))
          ).map((number: any) => (
            <li
              key={`k-${number}`}
              className={paging.currentPage === number ? 'active' : ''}
              data-cy={`table-pagination-${number}`}
              onClick={handleChangePage}
              data-number={number}
            >
              <span className={number !== '...' && number !== '..' ? '' : 'noPointer'}>
                {number === '..' ? '...' : number}
              </span>
            </li>
          ))}
        {paging.currentPage < paging.pagesTotal && (
          <li
            className="prev-next next"
            data-type="next"
            onClick={handleNextPrevPage}
            data-cy="table-pagination-next"
          >
            <a href="#" title={f({ id: 'table.pagination.next' })}>
              <i className="cmd-icon cmd-icon-expander-arrow-right" />
            </a>
          </li>
        )}
      </ul>

      <div className={paging.pagesTotal > 1 ? 'pagination-position' : 'pull-right'}>
        <span
          className="d-inline-block position-relative top-1 mr-2"
          data-cy={handleDataCy(props.tableName)}
        >
          {paging.rowsTotal} <FormattedMessage id="table.results" />:
        </span>
        <div className={`dropup d-inline-block ${isMenuOpen ? 'open' : ''}`}>
          <button
            onClick={handleOpenList}
            className="e-button dropdown-toggle"
            data-cy="table-pagination-total"
            title={f({ id: 'table.pagination.total' })}
            type="button"
            id="dropdownMenu19"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="true"
          >
            <FormattedMessage id="table.show" />
            <strong className="pagingList" style={{ marginLeft: '5px' }}>
              {`${paging.rowsPerPage}`}
            </strong>{' '}
            <i className="cmd-icons cmd-icon-dropdown ml-2 fs-4" />
          </button>
          <div
            className="dropdown-menu min-w-100 dropdown-menu-custom fs-2"
            aria-labelledby="dropdownMenu1"
            style={{ marginBottom: '15px' }}
          >
            {list.map((number: any) => (
              // <Link key={`page-number-${number}`} href="">
              <a
                href="#"
                key={`page-number-${number}`}
                title={number}
                data-cy={`table-pagination-${number}`}
                className={`px-2 py-xs d-block text-black  ${
                  number % 20 === 0 ? 'bg-gray-light' : ''
                }`}
                onClick={handleSetPage}
                data-number={number}
              >
                {number}
              </a>
              // </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
}
